import React from "react";
import { Link } from "react-router-dom";
import {
  Stethoscope,
  User,
  Calendar,
  ClipboardList,
  PlusCircle,
  Activity,
  RefreshCw,
  Search,
  AlertCircle,
  TrendingUp
} from "lucide-react";
import { useUserProfile } from "../../hooks/useUserProfile";
import { useDoctorStats } from "../../hooks/useDoctorStats";

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Bonjour";
  if (hour < 18) return "Bon après-midi";
  return "Bonsoir";
}

const DashboardHomeDoctor: React.FC = () => {
  const { userData } = useUserProfile();
  const { data, isLoading, isRefetching: refreshing, refetch } = useDoctorStats();

  const doctorName =
    userData?.fullName ||
    (userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : userData?.username) ||
    localStorage.getItem("userLabel") ||
    "Docteur";

  if (isLoading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full animate-pulse flex items-center justify-center mb-6">
            <Stethoscope className="w-10 h-10 text-white" />
          </div>
          <p className="text-lg text-gray-700">Chargement de votre espace docteur...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header avec effet glassmorphism amélioré */}
        <div className="mb-8 sm:mb-10 bg-white/80 backdrop-blur-2xl rounded-[2rem] p-6 sm:p-8 shadow-2xl border border-white/60 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-3xl -z-0"></div>
          <div className="relative z-10 flex flex-col gap-6">
            {/* Message de bienvenue centré */}
            <div className="text-center">
              <div className="flex items-center justify-center gap-4 mb-3">
                <div className="p-3 bg-gradient-to-br from-blue-600 via-cyan-600 to-blue-700 rounded-2xl shadow-xl">
                  <Stethoscope className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-black bg-gradient-to-r from-blue-700 via-cyan-600 to-indigo-600 bg-clip-text text-transparent tracking-tight">
                  {getGreeting()}, Dr {doctorName}
                </h1>
              </div>
              <p className="text-gray-600 text-base md:text-lg font-medium">
                Gérez vos consultations, patients et rendez-vous en temps réel
              </p>
            </div>

            {/* Barre de recherche et actualiser en dessous */}
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <div className="relative group">
                <input
                  type="text"
                  placeholder="Rechercher un patient..."
                  className="pl-12 pr-5 py-3.5 rounded-2xl border-2 border-gray-200/70 bg-white/90 backdrop-blur-sm shadow-md focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none w-full sm:w-72 transition-all group-hover:border-blue-300 group-hover:shadow-lg"
                />
                <Search className="absolute left-4 top-4 text-gray-400 group-hover:text-blue-500 transition-colors" size={20} />
              </div>
              <button
                onClick={() => refetch()}
                disabled={refreshing}
                className="flex items-center gap-2.5 px-7 py-3.5 bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 text-white rounded-2xl font-bold hover:shadow-2xl hover:shadow-blue-500/30 hover:scale-105 transition-all disabled:opacity-60 disabled:hover:scale-100"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`} />
                Actualiser
              </button>
            </div>
          </div>
        </div>
        {/* Statistiques */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <div className="bg-white rounded-2xl p-6 shadow-md border border-blue-100 hover:shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Activity className="w-8 h-8 text-blue-600" />
              </div>
              {data.myPendingConstantes > 0 && <AlertCircle className="w-6 h-6 text-red-500" />}
            </div>
            <p className="text-sm font-medium text-gray-600">Consultations aujourd'hui</p>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">{data.todayConsultations}</p>
            {data.myPendingConstantes > 0 && (
              <p className="text-sm text-red-600 mt-1">{data.myPendingConstantes} en attente</p>
            )}
          </div>

          <Link to="#" className="bg-white rounded-2xl p-6 shadow-md border border-green-100 hover:shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-xl">
                <User className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-600">Patients suivis</p>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">{data.totalPatients}</p>
          </Link>

          <Link to="#" className="bg-white rounded-2xl p-6 shadow-md border border-yellow-100 hover:shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-yellow-100 rounded-xl">
                <Calendar className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-600">RDV à venir (48h)</p>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">{data.upcomingAppointments}</p>
          </Link>

          <Link to="/doctor/dashboard/constante" className="bg-white rounded-2xl p-6 shadow-md border border-pink-100 hover:shadow-xl transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-pink-100 rounded-xl">
                <ClipboardList className="w-8 h-8 text-pink-600" />
              </div>
              {data.pendingExams > 0 && <AlertCircle className="w-6 h-6 text-red-500" />}
            </div>
            <p className="text-sm font-medium text-gray-600">Examens en attente</p>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">{data.pendingExams}</p>
          </Link>
        </div>

        {/* Raccourcis rapides */}
        <div className="mb-10">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Actions rapides</h2>
          {/* BOUTON CENTRAL */}
          <div className="mb-12 text-center">
            <Link
              to="/doctor/dashboard/constante"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-600 to-blue-700 text-white rounded-full px-6 sm:px-8 py-4 sm:py-5 text-2xl sm:text-3xl font-bold shadow-2xl hover:shadow-3xl hover:scale-105 transition-all duration-300 group"
            >
              <PlusCircle className="w-8 h-8 group-hover:rotate-90 transition-transform" />
              <span>Nouvelle consultation</span>
              <TrendingUp className="w-6 h-6" />
            </Link>
          </div>
        </div>

        {/* Alerte urgente */}
        {data.myPendingConstantes > 0 && (
          <div className="bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl p-1 shadow-2xl mb-10">
            <div className="bg-white rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <AlertCircle className="w-12 h-12 text-red-600" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Action requise</h3>
                    <p className="text-2xl sm:text-3xl font-bold text-red-600 mt-2">
                      {data.myPendingConstantes} patient(s) en attente de consultation
                    </p>
                  </div>
                </div>
                <Link
                  to="/doctor/dashboard/constante"
                  className="px-6 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all"
                >
                  Traiter maintenant
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Dernières consultations */}
        <div>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Dernières consultations</h2>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <p className="text-gray-500 italic text-center py-8">
              Fonctionnalité en cours de développement
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHomeDoctor;